import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import React, {FunctionComponent, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import cn from 'classnames';

import {ServerEndpointGetName, SSEEventType, SSEStatus, TestStatus} from 'enums';
import {ISSEEvent} from 'interfaces';
import {RunGroupsService, TestsService} from 'services';

interface ITestEventProps {
    // Events never change, so we're just passing the whole thing instead of individual props.
    event: ISSEEventListItem,
}

// Extend the ISSEEvent type to add props we need for the list
export type ISSEEventListItem = ISSEEvent & {
    id: number,
}

export const TestEvent: FunctionComponent<ITestEventProps> = (props) => {
    const e = props.event;
    const {t, i18n} = useTranslation();
    const [expanded, setExpanded] = useState(false);

    const toggleOpen = () => {
        setExpanded(!expanded);
    }

    let classNames: string[] = ['testEvent'];
    const className_runGroup = 'testEvent_runGroup';
    const className_testPass = 'testEvent_pass';
    const className_testFail = 'testEvent_fail';
    const className_validationSummary = 'testEvent_validationSummary';
    let content: JSX.Element | JSX.Element[];

    // We're ignoring a few event types, like "GotReboot". For those cases, we'll return "null" to render nothing.
    switch (e.event_type) {
        case SSEEventType.EndpointRequest:
            content = (<>
                <div className="testEvent_flexContainer">
                    <span className="testEvent_flexLabel testEvent_label_endpointRequest">
                        {ServerEndpointGetName(e.endpoint)}
                    </span>
                    {e.request_body && (
                        <button type="button" className="testEvent_expandButton" onClick={toggleOpen}>
                            {!expanded && (<FontAwesomeIcon icon="caret-right" className="testEvent_expandButtonIcon" />)}
                            {expanded && (<FontAwesomeIcon icon="caret-down" className="testEvent_expandButtonIcon" />)}
                            {t('tests.events.buttonExpandRequest')}
                        </button>
                    )}
                </div>
                {expanded && (
                    <div className="testEvent_json">
                        {e.request_body}
                    </div>
                )}
            </>);
            break;
        case SSEEventType.EndpointResponse: 
            const isCodeSuccess = e.status_code >= 200 && e.status_code < 300;
            content = (<>
                <div className="testEvent_flexContainer">
                    <span className="testEvent_flexLabel testEvent_label_endpointResponse">
                        {ServerEndpointGetName(e.endpoint)}&nbsp;-&nbsp;{t('tests.events.responseCode')}
                        <span className={isCodeSuccess ? 'testEvent_statusCodeSuccess' : 'testEvent_statusCodeError'}>
                            {e.status_code}
                        </span>
                    </span>
                    {e.response && (
                        <button type="button" className="testEvent_expandButton" onClick={toggleOpen}>
                            {!expanded && (<FontAwesomeIcon icon="caret-right" className="testEvent_expandButtonIcon" />)}
                            {expanded && (<FontAwesomeIcon icon="caret-down" className="testEvent_expandButtonIcon" />)}
                            {t('tests.events.buttonExpandResponse')}
                        </button>
                    )}
                </div>
                {expanded && (
                    <div className="testEvent_json">
                        {e.response}
                    </div>
                )}
            </>);
            break;
        case SSEEventType.Error:
            content = (<span className="testEvent_serverError">{e.message}</span>);
            break;
        case SSEEventType.EventMessageValidated: 
            if(e.pass) {
                content = (<span className="testEvent_pass">PASS: {e.topic}</span>);
            } else {
                content = (<span className="testEvent_serverError testEvent_fail">FAIL: {e.topic}: {e.error}</span>);
            }
            break;
        case SSEEventType.EventMessageValidationSummary:
            if (!e.results) break;
            classNames.push(className_validationSummary);
            let contentList = [(<span>Validation Summary</span>)];
            for(const result of e.results) {
                if(result.count == 0) {
                    contentList.push(<span className="testEvent_notTested">SCHEMA NOT TESTED: '{result.name}'</span>);
                } else if(result.pass) {
                    contentList.push(<span className="testEvent_pass">PASS: '{result.name}'</span>);
                } else {
                    contentList.push(<span className="testEvent_serverError testEvent_fail">FAIL: '{result.name}'</span>);
                }
            }
            contentList.push(<br />);

            content = contentList;
            break;
        case SSEEventType.Info:
            content = (<span>{e.message}</span>);
            break;
        case SSEEventType.MQTTConnect:
            content = (<span className="testEvent_mqtt">
                {e.blocked ? t('tests.events.mqttConnectBlocked') : t('tests.events.mqttConnect')}
            </span>);
            break;
        case SSEEventType.MQTTMessage:
            content = (<span className="testEvent_mqtt">
                {t('tests.events.mqttMessage', {topic: e.topic, payload: e.payload})}
            </span>);
            break;
        case SSEEventType.MQTTSubscribe:
            content = (<span className="testEvent_mqtt">
                {t('tests.events.mqttSubscribe', {topic: e.topic})}
            </span>);
            break;
        case SSEEventType.NeedReboot:
            content = (<span className="testEvent_mqtt">
                {t('tests.events.needReboot')}
            </span>);
            break;
        case SSEEventType.RunGroupStart:
            classNames.push(className_runGroup)
            content = (<span className="testEvent_mqtt">
                {t('tests.events.testGroupStart', {testGroupName: RunGroupsService.getName(e.run_group_id)})}
            </span>);
            break;
        case SSEEventType.RunGroupStop:
            classNames.push(className_runGroup)
            content = (<span className="testEvent_mqtt">
                {t('tests.events.testGroupStop', {testGroupName: RunGroupsService.getName(e.run_group_id)})}
            </span>);
            break;
        case SSEEventType.RunStart:
            content = (<span className="testEvent_mqtt">
                {t('tests.events.runStart')}
            </span>);
            break;
        case SSEEventType.RunStop:
            content = (<span className="testEvent_mqtt">
                {t('tests.events.runStop')}
            </span>);
            break;
        case SSEEventType.TestState:
            // We only want to show when tests pass or fail because it's too much visual noise to show when tests start.
            // A user can already tell when a test starts because we show a message for the run group.
            if (e.status === TestStatus.Passed) {
                classNames.push(className_testPass);
                content = (<span className="testEvent_mqtt">
                {t('tests.events.testPass', {testName: TestsService.getTestName(e.test_id)})}
            </span>);
            } else if (e.status === TestStatus.Failed) {
                classNames.push(className_testFail);
                content = (<span className="testEvent_mqtt">
                {t('tests.events.testFail', {testName: TestsService.getTestName(e.test_id), failReason: e.result})}
            </span>);
            }
            break;
    }

    if (content) return (
        <div className={cn(classNames)}>
            {content}
        </div>
    );
    return null;
};
